/** Product catalog search helpers ported from the prototype (matchesProduct, findProductByCode, saleSearchRank…). */

export interface CatalogProduct {
  id: string;
  code: string;
  code2: string;
  name: string;
  unit: string;
  buy: number;
  sell: number;
  local: number;
  warehouse: number;
  total: number;
}

/** Prototype `normalizedProductName(name)`: strip accents, lower-case, collapse non-alphanumerics to single spaces. */
export function normalizedProductName(name: string | null | undefined): string {
  return String(name || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Prototype `matchesProduct(p, q)` — `q` is compared lower-cased against name, code and code2. */
export function matchesProduct(p: Pick<CatalogProduct, 'name' | 'code' | 'code2'>, q: string): boolean {
  const query = String(q || '').toLowerCase();
  if (!query) return true;
  return (
    p.name.toLowerCase().includes(query) ||
    String(p.code || '')
      .toLowerCase()
      .includes(query) ||
    String(p.code2 || '')
      .toLowerCase()
      .includes(query)
  );
}

/** Prototype `findProductByCode(code)` — exact match on code or code2, first hit wins. */
export function findProductByCode<T extends Pick<CatalogProduct, 'code' | 'code2'>>(
  products: readonly T[],
  code: string,
): T | null {
  const c = String(code ?? '');
  if (!c) return null;
  return products.find((p) => p.code === c || p.code2 === c) ?? null;
}

/** Prototype `findProductByNormalizedName(name)`. */
export function findProductByNormalizedName<T extends Pick<CatalogProduct, 'name'>>(
  products: readonly T[],
  name: string,
): T | null {
  const key = normalizedProductName(name);
  if (!key) return null;
  return products.find((p) => normalizedProductName(p.name) === key) ?? null;
}

/** Prototype `saleSearchRank(name, q)`: 0 prefix match, 1 word-prefix match, 2 otherwise. */
export function saleSearchRank(name: string, q: string): 0 | 1 | 2 {
  const n = normalizedProductName(name);
  const x = normalizedProductName(q);
  if (!x) return 2;
  if (n.startsWith(x)) return 0;
  if (n.split(' ').some((w) => w.startsWith(x))) return 1;
  return 2;
}

export interface SearchIndexEntry<T> {
  product: T;
  nameLower: string;
  codeLower: string;
  code2Lower: string;
  normalized: string;
}

/** Builds a lower-cased index once so repeated searches over 2,000+ products stay well under 100 ms. */
export function buildSearchIndex<T extends Pick<CatalogProduct, 'name' | 'code' | 'code2'>>(
  products: readonly T[],
): SearchIndexEntry<T>[] {
  return products.map((product) => ({
    product,
    nameLower: product.name.toLowerCase(),
    codeLower: String(product.code || '').toLowerCase(),
    code2Lower: String(product.code2 || '').toLowerCase(),
    normalized: normalizedProductName(product.name),
  }));
}

function rankNormalized(normalized: string, x: string): 0 | 1 | 2 {
  if (!x) return 2;
  if (normalized.startsWith(x)) return 0;
  if (normalized.split(' ').some((w) => w.startsWith(x))) return 1;
  return 2;
}

/**
 * Ranked search exactly like the prototype's renderSaleProducts: filter with matchesProduct, an exact
 * code/code2 match (the scanned barcode) always comes first, then sort by saleSearchRank and finally by name
 * (sq locale). `limit` caps the returned rows (MAX_TABLE_ROWS in the UI).
 */
export function searchProducts<T extends Pick<CatalogProduct, 'name' | 'code' | 'code2'>>(
  index: readonly SearchIndexEntry<T>[],
  query: string,
  limit = Number.POSITIVE_INFINITY,
): T[] {
  const raw = String(query || '').trim();
  const q = raw.toLowerCase();
  const x = normalizedProductName(q);
  // an exact code always also matches textually (codeLower includes q), so it is already among the hits
  const exact = raw
    ? (index.find((e) => e.product.code === raw || e.product.code2 === raw)?.product ?? null)
    : null;
  const hits: { product: T; rank: number; nameLower: string }[] = [];
  for (const e of index) {
    // The name is matched on its accent-stripped form, so typing "pompe" finds "Pompë uji". It
    // REPLACES the raw check rather than joining it: normalising only strips diacritics and collapses
    // punctuation, so it matches everything the raw text would and more. Adding a fourth scan per
    // product instead cost this loop its budget on a 2,051-row catalogue. Codes stay raw — a barcode
    // is matched as typed. A query that normalises to nothing (punctuation only) keeps the raw check.
    if (
      q &&
      !(
        (x !== '' ? e.normalized.includes(x) : e.nameLower.includes(q)) ||
        e.codeLower.includes(q) ||
        e.code2Lower.includes(q)
      )
    )
      continue;
    hits.push({ product: e.product, rank: q ? rankNormalized(e.normalized, x) : 2, nameLower: e.nameLower });
  }
  hits.sort((a, b) => {
    if (exact) {
      if (a.product === exact) return -1;
      if (b.product === exact) return 1;
    }
    return a.rank - b.rank || a.nameLower.localeCompare(b.nameLower, 'sq');
  });
  return (Number.isFinite(limit) ? hits.slice(0, limit) : hits).map((h) => h.product);
}

/** Prototype `nameHash32(value)` — FNV-1a 32-bit. */
export function nameHash32(value: string | null | undefined): number {
  const s = String(value || '');
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

/** Prototype `generateCode()`: '383001' + 6 random digits (100000–999999). Random source injectable for tests. */
export function generateCode(random: () => number = Math.random): string {
  const base = '383001';
  return base + String(Math.floor(100000 + random() * 899999));
}

/**
 * Prototype `barcodeForProductName(name, lineId)`: deterministic '3832' + 8 digits derived from the normalized
 * name, reusing an existing product's code when the name already exists and avoiding collisions with other
 * products or other purchase lines.
 */
export function barcodeForProductName(
  name: string,
  products: readonly Pick<CatalogProduct, 'code' | 'name'>[],
  otherLines: readonly { code: string; name: string }[] = [],
  random: () => number = Math.random,
): string {
  const key = normalizedProductName(name);
  if (!key) return generateCode(random);
  const existing = findProductByNormalizedName(products, name);
  if (existing?.code) return String(existing.code);
  for (let salt = 0; salt < 100; salt++) {
    const h = nameHash32(key + (salt ? '#' + salt : ''));
    const code = '3832' + String(h % 100000000).padStart(8, '0');
    const owner = products.find((p) => String(p.code) === code);
    const lineOwner = otherLines.find((l) => String(l.code) === code);
    const sameName = (x: { name: string } | undefined) =>
      x !== undefined && normalizedProductName(x.name) === key;
    if (owner === undefined && lineOwner === undefined) return code;
    if (sameName(owner) || sameName(lineOwner)) return code;
  }
  return generateCode(random);
}
